import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  BarChart3,
  CalendarCheck,
  ChevronRight,
  Eye,
  Loader2,
  MapPin,
  MessageCircle,
  MousePointerClick,
  Phone,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import {
  GBP_PERF_RANGES,
  pctChange,
  useGbpPerformance,
  useGbpPerformanceSet,
  type GbpPerfDay,
  type GbpPerfRange,
  type GbpPerfTotals,
} from '../hooks/useGbpPerformance';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/** 1272 → "1,272", 15400 → "15.4K", 2300000 → "2.3M" */
function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function fmtDate(iso: string, opts: Intl.DateTimeFormatOptions): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, opts);
}

function Delta({ value }: { value: number | null }) {
  if (value == null) return null;
  const rounded = Math.round(Math.abs(value));
  if (rounded === 0) return <span className="text-[10px] font-semibold text-gray-400">—</span>;
  return (
    <span
      className={`text-[10px] font-semibold shrink-0 ${value > 0 ? 'text-green-600' : 'text-red-500'}`}
    >
      {value > 0 ? '▲' : '▼'}
      {rounded}%
    </span>
  );
}

const ACTION_SERIES: Array<{ key: keyof GbpPerfTotals; label: string; color: string; icon: typeof Phone }> = [
  { key: 'calls', label: 'Calls', color: '#1a73e8', icon: Phone },
  { key: 'websiteClicks', label: 'Website clicks', color: '#12b76a', icon: MousePointerClick },
  { key: 'directionRequests', label: 'Directions', color: '#f79009', icon: MapPin },
  { key: 'bookings', label: 'Bookings', color: '#7a5af8', icon: CalendarCheck },
  { key: 'conversations', label: 'Messages', color: '#ee46bc', icon: MessageCircle },
];

const IMPRESSION_SERIES: Array<{ key: keyof GbpPerfTotals; label: string; color: string }> = [
  { key: 'impressionsMobileSearch', label: 'Search · mobile', color: '#1a73e8' },
  { key: 'impressionsDesktopSearch', label: 'Search · desktop', color: '#8ab4f8' },
  { key: 'impressionsMobileMaps', label: 'Maps · mobile', color: '#12b76a' },
  { key: 'impressionsDesktopMaps', label: 'Maps · desktop', color: '#a6f4c5' },
];

type Bucket = { label: string; full: string; values: Record<string, number> };

/** Roll daily rows into day / week / month buckets so long ranges stay legible. */
function bucketDays(days: GbpPerfDay[], range: GbpPerfRange): Bucket[] {
  const mode =
    range === 'mtd' || range === '7d' || range === '28d'
      ? 'day'
      : range === '90d' || range === '6m'
        ? 'week'
        : 'month';
  const buckets = new Map<string, Bucket>();
  for (const d of days) {
    const [y, m, dd] = d.date.split('-').map(Number);
    const date = new Date(y, m - 1, dd);
    let key: string;
    let label: string;
    let full: string;
    if (mode === 'day') {
      key = d.date;
      label = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      full = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } else if (mode === 'week') {
      const monday = new Date(date);
      monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
      key = monday.toISOString().slice(0, 10);
      label = monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      full = `Week of ${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    } else {
      key = `${y}-${String(m).padStart(2, '0')}`;
      label = date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
      full = date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    const b = buckets.get(key) || { label, full, values: {} };
    for (const [k, v] of Object.entries(d)) {
      if (k === 'date') continue;
      b.values[k] = (b.values[k] || 0) + (Number(v) || 0);
    }
    buckets.set(key, b);
  }
  return [...buckets.values()];
}

function StackedChart({
  buckets,
  series,
  emptyLabel,
}: {
  buckets: Bucket[];
  series: Array<{ key: string; label: string; color: string }>;
  emptyLabel: string;
}) {
  const active = series.filter((s) => buckets.some((b) => (b.values[s.key] || 0) > 0));
  if (active.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-10">{emptyLabel}</p>;
  }
  const data: ChartData<'bar', number[], string> = {
    labels: buckets.map((b) => b.label),
    datasets: active.map((s) => ({
      label: s.label,
      data: buckets.map((b) => b.values[s.key] || 0),
      backgroundColor: s.color,
      borderRadius: 3,
      maxBarThickness: 18,
    })),
  };
  return (
    <>
      <div className="h-[210px] w-full">
        <Bar
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const b = buckets[items[0]?.dataIndex ?? -1];
                    if (!b) return '';
                    const total = active.reduce((sum, s) => sum + (b.values[s.key] || 0), 0);
                    return `${b.full} — ${total.toLocaleString()}`;
                  },
                  label: (ctx) => `${ctx.dataset.label}: ${(ctx.raw as number).toLocaleString()}`,
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                grid: { display: false },
                ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 } },
              },
              y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: { font: { size: 10 }, precision: 0 },
              },
            },
          }}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
        {active.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}

function KpiCard({
  label,
  value,
  delta,
  hint,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  delta: number | null;
  hint?: string;
  icon?: typeof Phone;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-0">
      <div className="flex items-center gap-1.5 text-[11px] text-gray-500 min-w-0">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" style={accent ? { color: accent } : undefined} /> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5 min-w-0">
        <span className="text-[19px] font-bold text-gray-900 leading-tight tabular-nums truncate">
          {fmtCount(value)}
        </span>
        <Delta value={delta} />
      </div>
      {hint ? <p className="text-[10px] text-gray-400 truncate mt-0.5">{hint}</p> : null}
    </div>
  );
}

// ── Compact strip (lives on the workspace above the feed) ────────────────────

export function GoogleBusinessInsightsStrip({
  businessId,
  connectionId,
  onOpen,
}: {
  businessId: string;
  connectionId: string;
  onOpen: () => void;
}) {
  const { data, loading, error, unsupported } = useGbpPerformance(businessId, connectionId, 'mtd');
  if (unsupported || error) return null;
  if (loading && !data) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white px-3 py-3 flex items-center gap-2 text-[12px] text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading Google insights…
      </section>
    );
  }
  if (!data) return null;
  const t = data.totals;
  const p = data.previousTotals;
  const cards: Array<[string, number, number | null]> = [
    ['Views', t.impressions, pctChange(t.impressions, p?.impressions)],
    ['Calls', t.calls, pctChange(t.calls, p?.calls)],
    ['Website', t.websiteClicks, pctChange(t.websiteClicks, p?.websiteClicks)],
    ['Directions', t.directionRequests, pctChange(t.directionRequests, p?.directionRequests)],
    ['Bookings', t.bookings, pctChange(t.bookings, p?.bookings)],
    ['Messages', t.conversations, pctChange(t.conversations, p?.conversations)],
  ];
  const monthLabel = fmtDate(data.start, { month: 'long' });
  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 min-w-0 truncate">
          {monthLabel} so far on Google
          {data.dataThrough ? (
            <span className="ml-1.5 font-medium normal-case tracking-normal text-gray-400">
              · through {fmtDate(data.dataThrough, { month: 'short', day: 'numeric' })}
            </span>
          ) : (
            <span className="ml-1.5 font-medium normal-case tracking-normal text-gray-400">
              · nothing published yet
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-[#1a73e8] hover:underline"
        >
          Full insights
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="grid grid-cols-3 gap-1.5 sm:gap-2 w-full text-left"
        aria-label="Open Google Business insights"
      >
        {cards.map(([label, value, delta]) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-0"
          >
            <div className="flex items-baseline gap-1 min-w-0">
              <span className="text-[15px] font-bold text-gray-900 leading-tight truncate tabular-nums">
                {fmtCount(value)}
              </span>
              <Delta value={delta} />
            </div>
            <p className="text-[10px] text-gray-500 truncate mt-0.5">{label}</p>
          </div>
        ))}
      </button>
    </section>
  );
}

// ── Full insights sheet ──────────────────────────────────────────────────────

export function GoogleBusinessInsightsSheet({
  businessId,
  connectionId,
  connectionIds,
  locationName,
  open,
  onClose,
}: {
  businessId: string;
  connectionId?: string | null;
  /** When set, the sheet sums these locations. Used for the All businesses view. */
  connectionIds?: string[];
  locationName?: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [range, setRange] = useState<GbpPerfRange>('mtd');
  const [chart, setChart] = useState<'views' | 'actions'>('views');
  const ids =
    connectionIds && connectionIds.length > 0
      ? connectionIds
      : connectionId
        ? [connectionId]
        : [];
  const { data, loading, error, unsupported, refresh } = useGbpPerformanceSet(
    businessId,
    ids,
    range,
    { enabled: open },
  );

  const buckets = useMemo(() => (data ? bucketDays(data.days, data.range) : []), [data]);

  if (!open) return null;

  const t = data?.totals;
  const p = data?.previousTotals;
  const rangeLabel = GBP_PERF_RANGES.find((r) => r.key === range)?.label || range;

  return createPortal(
    <div className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-xl overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Google Business insights"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Google Business insights
            </h3>
            {locationName ? (
              <p className="text-[11px] text-gray-500 truncate">{locationName}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              aria-label="Refresh from Google"
              className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-4 pt-3 shrink-0">
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {GBP_PERF_RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                  range === r.key
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 overflow-y-auto space-y-5">
          {unsupported ? (
            <p className="text-sm text-gray-500 text-center py-10">
              Insights need the direct Google connection. Reconnect Google Business Profile to
              unlock them.
            </p>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-10">{error}</p>
          ) : !data ? (
            <div className="flex justify-center py-16 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              {data.metricsError ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                  Google could not refresh performance right now ({data.metricsError}). Showing the
                  last saved numbers.
                </div>
              ) : null}

              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-gray-500">
                  {fmtDate(data.start, {
                    month: 'short',
                    day: 'numeric',
                    ...(data.start.slice(0, 4) !== data.end.slice(0, 4) ? { year: 'numeric' } : {}),
                  })}{' '}
                  – {fmtDate(data.end, { month: 'short', day: 'numeric', year: 'numeric' })}
                  {p && data.previousRange ? (
                    <span className="text-gray-400">
                      {' '}
                      · vs {fmtDate(data.previousRange.start, { month: 'short', day: 'numeric' })} –{' '}
                      {fmtDate(data.previousRange.end, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : null}
                </p>
                <p className="text-[10px] text-gray-400">
                  {data.dataThrough ? (
                    <>
                      Google has published through{' '}
                      <span className="font-semibold text-gray-600">
                        {fmtDate(data.dataThrough, { month: 'short', day: 'numeric' })}
                      </span>
                      {data.dataThrough < data.end ? ' — later days fill in as Google releases them' : ''}
                    </>
                  ) : (
                    'Google has not published any days in this range yet'
                  )}
                </p>
              </div>

              {/* Discovery */}
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  How people found you
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <KpiCard
                    label="Profile views"
                    value={t!.impressions}
                    delta={pctChange(t!.impressions, p?.impressions)}
                    icon={Eye}
                    accent="#1a73e8"
                    hint={`${rangeLabel}`}
                  />
                  <KpiCard
                    label="Google Search"
                    value={t!.impressionsSearch}
                    delta={pctChange(t!.impressionsSearch, p?.impressionsSearch)}
                    icon={Search}
                    accent="#1a73e8"
                    hint={
                      t!.impressions > 0
                        ? `${Math.round((t!.impressionsSearch / t!.impressions) * 100)}% of views`
                        : undefined
                    }
                  />
                  <KpiCard
                    label="Google Maps"
                    value={t!.impressionsMaps}
                    delta={pctChange(t!.impressionsMaps, p?.impressionsMaps)}
                    icon={MapPin}
                    accent="#12b76a"
                    hint={
                      t!.impressions > 0
                        ? `${Math.round((t!.impressionsMaps / t!.impressions) * 100)}% of views`
                        : undefined
                    }
                  />
                </div>
              </section>

              {/* Actions */}
              <section className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    What they did next
                  </p>
                  <p className="text-[11px] text-gray-500">
                    <span className="font-semibold text-gray-900 tabular-nums">
                      {fmtCount(t!.interactions)}
                    </span>{' '}
                    interactions
                    {t!.impressions > 0 ? (
                      <span className="text-gray-400">
                        {' '}
                        · {((t!.interactions / t!.impressions) * 100).toFixed(1)}% of views
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {ACTION_SERIES.map((s) => (
                    <KpiCard
                      key={s.key}
                      label={s.label}
                      value={t![s.key]}
                      delta={pctChange(t![s.key], p?.[s.key])}
                      icon={s.icon}
                      accent={s.color}
                    />
                  ))}
                </div>
              </section>

              {/* Trend chart */}
              <section>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Trend
                  </p>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-[11px] font-semibold">
                    {(
                      [
                        ['views', 'Views'],
                        ['actions', 'Actions'],
                      ] as const
                    ).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setChart(key)}
                        className={`px-2.5 py-1 rounded-md transition-colors ${
                          chart === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3">
                  {chart === 'views' ? (
                    <StackedChart
                      buckets={buckets}
                      series={IMPRESSION_SERIES}
                      emptyLabel="No profile views recorded for this period yet."
                    />
                  ) : (
                    <StackedChart
                      buckets={buckets}
                      series={ACTION_SERIES}
                      emptyLabel="No customer actions recorded for this period yet."
                    />
                  )}
                </div>
              </section>

              {/* Search keywords */}
              <section>
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Searches that showed your profile
                  </p>
                  {data.keywordsPeriod && data.keywords.length > 0 ? (
                    <p className="text-[10px] text-gray-400">
                      {data.keywordsPeriod.start === data.keywordsPeriod.end
                        ? fmtDate(data.keywordsPeriod.start, { month: 'long', year: 'numeric' })
                        : `${fmtDate(data.keywordsPeriod.start, { month: 'short', year: 'numeric' })} – ${fmtDate(
                            data.keywordsPeriod.end,
                            { month: 'short', year: 'numeric' },
                          )}`}
                      {' · Google hides tiny counts'}
                    </p>
                  ) : null}
                </div>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  {data.keywordsError && data.keywords.length === 0 ? (
                    <p className="text-[12px] text-amber-700 px-3 py-4">
                      Search keywords aren't available yet ({data.keywordsError}).
                    </p>
                  ) : data.keywords.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8 px-4">
                      Google hasn't published search terms for{' '}
                      {data.keywordsPeriod && data.keywordsPeriod.start === data.keywordsPeriod.end
                        ? fmtDate(data.keywordsPeriod.start, { month: 'long' })
                        : 'this period'}{' '}
                      yet. Google releases each month's search terms a few days after the month ends.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {data.keywords.slice(0, 30).map((k, i) => {
                        const max = data.keywords[0]?.impressions || 1;
                        const width = k.impressions ? Math.max(4, (k.impressions / max) * 100) : 3;
                        return (
                          <li key={k.keyword} className="px-3 py-2">
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="text-[13px] text-gray-800 truncate">
                                <span className="text-gray-400 tabular-nums mr-2">{i + 1}.</span>
                                {k.keyword}
                              </span>
                              <span className="text-[12px] font-semibold text-gray-900 tabular-nums shrink-0">
                                {k.impressions != null
                                  ? `${k.approx ? '~' : ''}${k.impressions.toLocaleString()}`
                                  : `<${k.threshold ?? 15}`}
                              </span>
                            </div>
                            <div className="mt-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-[#1a73e8]/70"
                                style={{ width: `${width}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>

              <p className="text-[10px] text-gray-400 flex items-center gap-1.5">
                <BarChart3 className="h-3 w-3" />
                Source: Google Business Profile Performance API. Google publishes metrics with a
                delay of a few days and keeps about 18 months of history; Nexus saves it so your
                record keeps growing.
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
